import is from '@sindresorhus/is';
import { useMemo } from 'react';

import { theme } from '../../theme/cobrainer-theme';
import { isNonContributingSidepanel, isZeroWidth } from './tile-motion';
import { tileWidthVar } from './tile-resize';
import type { TileKind, TileWidthStyle } from './types';

type TileDef = {
    readonly id: string;
    readonly minWidth?: string;
    readonly maxWidth?: string;
    readonly width?: string;
    readonly kind?: TileKind;
    readonly resizeKey?: string;
};

const DEFAULT_SIDEPANEL_WIDTH = '50%';

const resolveSidepanelWidth = (tile: TileDef, isLargeScreen: boolean, isFirstSidepanel: boolean): string => {
    if (tile.kind === 'dock') {
        return tile.width ?? '0px';
    }

    if (isLargeScreen && isFirstSidepanel) {
        if (is.nonEmptyString(tile.maxWidth) && tile.maxWidth === tile.minWidth) {
            return tile.maxWidth;
        }

        if (is.nonEmptyString(tile.maxWidth)) {
            return `min(${DEFAULT_SIDEPANEL_WIDTH}, ${tile.maxWidth})`;
        }

        return DEFAULT_SIDEPANEL_WIDTH;
    }

    if (isLargeScreen) {
        return tile.maxWidth ?? DEFAULT_SIDEPANEL_WIDTH;
    }

    return tile.minWidth ?? DEFAULT_SIDEPANEL_WIDTH;
};

const buildMainContentWidth = (gapSize: string, contributingWidths: readonly string[]): string => {
    if (contributingWidths.length === 0) {
        return '100%';
    }

    const gaps = contributingWidths.map(() => gapSize);
    const subtractions = [...gaps, ...contributingWidths];

    return `calc(100% - ${subtractions.join(' - ')})`;
};

const getTileWidthStyle = (tile: TileDef, width: string, gapSize: string, isFirstPanel: boolean): TileWidthStyle => {
    if (isFirstPanel) {
        return {
            maxWidth: is.nonEmptyString(tile.maxWidth) ? `calc(${tile.maxWidth} - ${gapSize})` : undefined,
            minWidth: is.nonEmptyString(tile.minWidth) ? `calc(${tile.minWidth} - ${gapSize})` : undefined,
            width: `calc(${width} - ${gapSize})`,
        };
    }

    return {
        maxWidth: tile.maxWidth,
        minWidth: tile.minWidth,
        width,
    };
};

// A resizable panel keeps its own width as the floor and grows up to the visible surface beside the docks.
const buildResizedPanelMaxWidth = (
    gapSize: string,
    contributingTiles: readonly TileDef[],
    contributingWidths: readonly string[],
): string => {
    const dockWidths = contributingTiles.flatMap((tile, index) =>
        tile.kind === 'dock' ? [`${contributingWidths[index] ?? '0px'} - ${gapSize}`] : [],
    );

    return dockWidths.length === 0 ? '100%' : `calc(100% - ${dockWidths.join(' - ')})`;
};

const toResizableWidthStyle = (style: TileWidthStyle, resizeKey: string, maxWidth: string): TileWidthStyle => {
    const floor = style.minWidth ?? style.width;

    return {
        maxWidth,
        minWidth: floor,
        width: `clamp(${floor}, var(${tileWidthVar(resizeKey)}, ${style.width}), ${maxWidth})`,
    };
};

const getDefaultMainMinWidth = (isLargeScreen: boolean, maxWidth: string | undefined): string => {
    const floor = isLargeScreen ? '50%' : '400px';

    return is.nonEmptyString(maxWidth) ? `min(${floor}, ${maxWidth})` : floor;
};

const getSidepanelWidthStyle = (
    tile: TileDef,
    contributingTiles: readonly TileDef[],
    contributingWidths: readonly string[],
    firstPanelWidth: string | undefined,
    gapSize: string,
    isLargeScreen: boolean,
    resizedPanelMaxWidth: string,
): TileWidthStyle => {
    if (tile.kind === 'dock') {
        return {
            maxWidth: tile.maxWidth,
            minWidth: tile.minWidth,
            width: tile.width ?? '0px',
        };
    }

    if (isZeroWidth(tile.width)) {
        return { maxWidth: '0px', minWidth: '0px', width: '0px' };
    }

    const contributingIndex = contributingTiles.indexOf(tile);
    const isFirstPanel = contributingIndex === 0;
    const overrideWidth = contributingIndex === 0 && is.nonEmptyString(firstPanelWidth) ? firstPanelWidth : undefined;

    if (is.nonEmptyString(overrideWidth)) {
        const width = `calc(${overrideWidth} - ${gapSize})`;

        return { maxWidth: width, minWidth: width, width };
    }

    const sidepanelWidth = isLargeScreen ? (contributingWidths[contributingIndex] ?? '50%') : (tile.minWidth ?? '50%');
    const style = getTileWidthStyle(tile, sidepanelWidth, gapSize, isFirstPanel);

    return is.nonEmptyString(tile.resizeKey)
        ? toResizableWidthStyle(style, tile.resizeKey, resizedPanelMaxWidth)
        : style;
};

export const useTileWidths = (
    tiles: readonly TileDef[],
    isLargeScreen: boolean,
    firstPanelWidth?: string,
): ReadonlyMap<string, TileWidthStyle> => {
    return useMemo(() => {
        if (tiles.length === 1) {
            const [tile] = tiles;
            if (tile === undefined) {
                return new Map<string, TileWidthStyle>();
            }

            return new Map<string, TileWidthStyle>([
                [
                    tile.id,
                    {
                        maxWidth: tile.maxWidth,
                        minWidth: tile.minWidth,
                        width: '100%',
                    },
                ],
            ]);
        }

        const gapSize = theme.spacing.m;
        const sidepanelTiles = tiles.slice(1);
        const contributingTiles = sidepanelTiles.filter((tile) => !isNonContributingSidepanel(tile));
        const firstPanelOverride = (tile: TileDef, index: number): string | undefined =>
            index === 0 && tile.kind !== 'dock' && is.nonEmptyString(firstPanelWidth) ? firstPanelWidth : undefined;
        const contributingWidths = contributingTiles.map(
            (tile, index) => firstPanelOverride(tile, index) ?? resolveSidepanelWidth(tile, isLargeScreen, index === 0),
        );
        const resizedPanelMaxWidth = buildResizedPanelMaxWidth(gapSize, contributingTiles, contributingWidths);
        const sidepanelStyles = new Map(
            sidepanelTiles.map((tile) => [
                tile.id,
                getSidepanelWidthStyle(
                    tile,
                    contributingTiles,
                    contributingWidths,
                    firstPanelWidth,
                    gapSize,
                    isLargeScreen,
                    resizedPanelMaxWidth,
                ),
            ]),
        );
        // A resized panel takes Main's room with its dragged width. The first panel's style already
        // subtracts the gap, so the gap is added back to keep Main's arithmetic unchanged.
        const mainContributions = contributingTiles.map((tile, index) => {
            const contributingWidth = contributingWidths[index] ?? '0px';
            const resizedWidth = sidepanelStyles.get(tile.id)?.width;

            if (tile.kind === 'dock' || !is.nonEmptyString(tile.resizeKey) || is.undefined(resizedWidth)) {
                return contributingWidth;
            }

            return index === 0 ? `calc(${resizedWidth} + ${gapSize})` : resizedWidth;
        });
        const [mainTile] = tiles;

        if (is.undefined(mainTile)) {
            return sidepanelStyles;
        }

        return new Map<string, TileWidthStyle>([
            [
                mainTile.id,
                {
                    maxWidth: mainTile.maxWidth,
                    minWidth: mainTile.minWidth ?? getDefaultMainMinWidth(isLargeScreen, mainTile.maxWidth),
                    width: buildMainContentWidth(gapSize, mainContributions),
                },
            ],
            ...sidepanelStyles,
        ]);
    }, [tiles, isLargeScreen, firstPanelWidth]);
};
